import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ButtonComponent } from 'src/app/shared/components/inputs/button/button.component';
import { DatapickerInputComponent } from 'src/app/shared/components/inputs/datapicker-input/datapicker-input.component';
import { DatapickerInput } from 'src/utils/models/input.interface';
import { ToastrService } from 'ngx-toastr';
import dayjs from 'dayjs';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { UploadCardComponent, ExistingFile } from 'src/app/shared/components/inputs/upload-card/upload-card.component';

export interface StartExecutionModalData {
  title?: string;
  description?: string;
  /** Quantidade de séries que serão executadas (padrão: 1). */
  seriesCount?: number;
}

export interface StartExecutionModalResult {
  start_date?: string;
  end_date?: string;
  trigger_etl_dag?: boolean;
  file?: File;
}

@Component({
  selector: 'app-start-execution-modal',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    MatIconModule,
    ButtonComponent,
    DatapickerInputComponent,
    MatSlideToggleModule,
    UploadCardComponent,
  ],
  templateUrl: './start-execution-modal.component.html',
  styleUrls: ['./start-execution-modal.component.scss'],
})
export class StartExecutionModalComponent {
  startDateInput: DatapickerInput = {
    label: 'Data início',
    subLabel: '(opcional)',
    placeholder: 'Selecione a data de início',
    date: null,
  };

  endDateInput: DatapickerInput = {
    label: 'Data fim',
    subLabel: '(opcional)',
    placeholder: 'Selecione a data de fim',
    date: null,
  };

  triggerEtlDag = true;
  executionFile: File | null = null;
  executionExistingFile: ExistingFile | null = null;

  constructor(
    public dialogRef: MatDialogRef<StartExecutionModalComponent, StartExecutionModalResult>,
    @Inject(MAT_DIALOG_DATA) public data: StartExecutionModalData,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  get modalTitle(): string {
    return this.data?.title ?? 'Período da execução';
  }

  get modalDescription(): string {
    return this.data?.description ?? 'Informe o período desejado ou deixe em branco para processar a série completa.';
  }

  get fullLoadInfoMessage(): string {
    return 'Caso nenhuma data seja informada, será realizada a carga completa.';
  }

  get canConfirmExecution(): boolean {
    return this.getValidationMessage() === null;
  }

  get validationMessage(): string | null {
    return this.getValidationMessage();
  }

  onDateRangeChange(): void {
    this.cdr.markForCheck();
  }

  private getValidationMessage(): string | null {
    const startDate = this.startDateInput.date;
    const endDate = this.endDateInput.date;

    if (!startDate && endDate) {
      return 'Informe a data de início. Não é permitido informar apenas a data de fim.';
    }

    if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate), 'day')) {
      return 'A data de início não pode ser maior que a data de fim.';
    }

    return null;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    const validationMessage = this.getValidationMessage();
    if (validationMessage) {
      this.toastr.show(validationMessage, 'Atenção', {
        timeOut: 5000,
        messageClass: 'warning',
      });
      return;
    }

    const result: StartExecutionModalResult = {
      trigger_etl_dag: this.triggerEtlDag,
    };
    const startDate = this.formatDateForApi(this.startDateInput.date);
    const endDate = this.formatDateForApi(this.endDateInput.date);

    if (startDate) result.start_date = startDate;
    if (endDate) result.end_date = endDate;
    if (this.executionFile) result.file = this.executionFile;

    this.dialogRef.close(result);
  }

  onExecutionFileSelected(file: File): void {
    if (!file) return;
    this.executionFile = file;
    this.executionExistingFile = {
      _id: 'execution-file',
      fileName: file.name,
      createdAt: new Date().toISOString(),
      fileSize: file.size,
      fileDownloadLink: '',
      updatedBy: localStorage.getItem('name') || '',
    };
    this.cdr.markForCheck();
  }

  onExecutionFileDelete(): void {
    this.executionFile = null;
    this.executionExistingFile = null;
    this.cdr.markForCheck();
  }

  private formatDateForApi(date: Date | null | undefined): string | undefined {
    if (!date) return undefined;
    return dayjs(date).format('YYYY-MM-DD');
  }
}
